import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  useDisclosure,
} from '@nextui-org/react';
import Link from 'next/link';
import React from 'react';

import { IconMore } from '@/assets';
import { Images } from '@/components';
import ButtonViewMore from '@/components/ButtonViewMore';
import ClientOnly from '@/components/client-only';
import RenderWhen from '@/components/RenderWhen';
import useViewMore from '@/hooks/useViewMore';
import { useAuth } from '@/provider/UserProvider';
import type { ProfileSocial } from '@/types';
import type { CollectionData } from '@/types/collection';
import type { LinkSocialProfile } from '@/types/user';
import { getIconSocial } from '@/utils';

import DeleteCollection from './Delete';
import EditCollection from './Edit';
import TooltipCustom from '@/components/tooltip-custom';

const InfoCollection: React.FC<{
  collection: CollectionData;
}> = ({ collection }) => {
  const { isViewMore, isShowBtn, toggle } = useViewMore(128, 'collection-desc');
  const { auth } = useAuth();

  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isOpen: isOpenEdit,
    onOpen: onOpenEdit,
    onOpenChange: onOpenChangeEdit,
    onClose: onCloseEdit,
  } = useDisclosure();
  const isOwner = collection.accountAddress === auth.user.publicAddress;

  return (
    <div>
      <div className="relative flex h-[320px] w-full justify-center sm:h-[200px]">
        <Images
          className="z-0 h-full w-full object-cover"
          src={collection.banner ? collection.banner : '/images/defaulte_cover_profile.png'}
          alt="banner"
        />
        <p
          style={{
            background: 'linear-gradient(180deg, rgba(30, 30, 30, 0.00) 32.34%, #222 100%)',
          }}
          className="absolute top-0 z-10 h-full w-full"
        />
        <div className="main-container absolute bottom-6 z-20 flex w-full items-center justify-between">
          <div className="max-w-[700px]">
            <h1 className="text-[48px] font-bold text-white lg:text-[32px] sm:!text-[24px]">
              {collection?.name?.length > 25 ? (
                <span className="flex items-center">
                  {collection?.name.slice(0, 20)}
                  <TooltipCustom placement="top" content={collection?.name}>
                    <span className="cursor-pointer text-white">...</span>
                  </TooltipCustom>
                </span>
              ) : (
                collection?.name || 'No name'
              )}
            </h1>
            <p id="collection-desc" className="text-[16px] leading-[1.6] text-white sm:text-[14px]">
              {collection?.description}
            </p>
            <ClientOnly>
              <ButtonViewMore
                toggle={toggle}
                isViewMore={isViewMore}
                isShowBtn={isShowBtn}
                className="mt-3"
              />
            </ClientOnly>
          </div>
          <div className="flex items-center justify-end">
            <div className="mb-3 md:hidden">
              {collection?.links?.length > 0 && (
                <div className="flex items-center sm:hidden">
                  {collection?.links?.map((link) => (
                    <Link
                      className="mx-2 flex h-[40px] w-[40px] items-center justify-center rounded-[50%] bg-gray-100 duration-300 hover:bg-gray-200 sm:mx-0 sm:mr-2"
                      key={link.type}
                      href={link.url}
                      target="_blank"
                    >
                      {getIconSocial(link.type as ProfileSocial)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <ClientOnly>
              <RenderWhen>
                <RenderWhen.If isTrue={isOwner}>
                  <div className="self-end">
                    <Dropdown>
                      <DropdownTrigger>
                        <Button className="mb-3 ml-2 h-[40px] w-[40px] min-w-0 rounded-[100px] bg-gray-50 p-0 sm:h-[36px] sm:w-[36px]">
                          <IconMore />
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu aria-label="Static Actions">
                        <DropdownItem onClick={onOpenEdit} key="edit">
                          Edit collection details
                        </DropdownItem>
                        <DropdownItem key="delete" onClick={onOpen}>
                          Delete collection
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                  <DeleteCollection
                    collectionId={collection.id}
                    isOpen={isOpen}
                    onOpenChange={onOpenChange}
                  />
                  <EditCollection
                    isOpen={isOpenEdit}
                    onClose={onCloseEdit}
                    onOpenChange={onOpenChangeEdit}
                    collectionId={collection.id}
                    name={collection.name}
                    description={collection.description}
                    thumbnail={collection.picture}
                    banner={collection.banner}
                    tags={collection.tags}
                    links={collection?.links as LinkSocialProfile[]}
                  />
                </RenderWhen.If>
              </RenderWhen>
            </ClientOnly>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoCollection;
